<template>
  <div>
    <!-- Compromisso Table -->
    <compromisso-table
      ref="compromissoTableRef"
      @edit-compromisso="handleEditCompromisso"
      @edit-cliente="handleEditCliente"
      @checkout-compromisso="handleCheckoutCompromisso">
    </compromisso-table>

    <!-- Modals are centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import CompromissoTable from './CompromissoTable.vue';

type Id = number | string;

interface CompromissoTableInstance {
  loadData: () => void | Promise<void>;
}

interface Props {
  usuarioVersao?: number;
  translations?: Record<string, unknown>;
  datatablesLanguageUrl?: string;
}

withDefaults(defineProps<Props>(), {
  usuarioVersao: 0,
  translations: () => ({}),
  datatablesLanguageUrl: ''
});

// Reactive data
const compromissoTableRef = ref<CompromissoTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  if (compromissoTableRef.value) {
    await compromissoTableRef.value.loadData();
  }
};

const handleEditCompromisso = (compromissoId?: Id): void => {
  log('CompromissoPage', 'handleEditCompromisso', 'Opening edit modal for ID via eventBus', compromissoId);
  eventBus.emit('modal.compromisso.open', { id: compromissoId });
};

const handleEditCliente = (clienteId: Id): void => {
  log('CompromissoPage', 'handleEditCliente', 'Editing client ID via eventBus', clienteId);
  // ClienteModal listens to 'cliente.edit' and 'modal.cliente.open' events
  eventBus.emit('modal.cliente.open', { id: clienteId });
};

const handleCheckoutCompromisso = (compromissoId?: Id): void => {
  log('CompromissoPage', 'handleCheckoutCompromisso', 'Checkout via eventBus', compromissoId);
  eventBus.emit('modal.compromisso.checkout', { id: compromissoId });
};

const handleClienteSaved = (): void => {
  log('CompromissoPage', 'handleClienteSaved', 'Cliente saved, refreshing table');
  // Refresh compromisso table when cliente is updated
  if (compromissoTableRef.value) {
    compromissoTableRef.value.loadData();
  }
};

// Lifecycle
onMounted(() => {
  log('CompromissoPage', 'mounted', 'Component mounted');

  // Listen for cliente saved event to refresh table
  eventBus.on('cliente.saved', handleClienteSaved);

  // Set up eventBus listeners for backward compatibility
  eventBus.on('compromisso.edit', handleEditCompromisso);
});

onBeforeUnmount(() => {
  log('CompromissoPage', 'beforeUnmount', 'Cleaning up event listeners');

  eventBus.off('cliente.saved', handleClienteSaved);
  eventBus.off('compromisso.edit', handleEditCompromisso);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleEditCompromisso,
  handleEditCliente,
  handleCheckoutCompromisso
});
</script>

<style scoped>
:deep(.pi-spin) {
  animation: pi-spin 2s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>